import React, { useEffect, useRef, useState } from "react";

/**
 * Count up from 0 to `to` when the element scrolls into view.
 * Uses IntersectionObserver + requestAnimationFrame — no external deps.
 */
const CountUp = ({ to = 100, duration = 1600, className = "", prefix = "", suffix = "", "data-testid": testId }) => {
  const [value, setValue] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || started) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setStarted(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      // easeOutCubic for a natural feel
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(eased * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started, to, duration]);

  return (
    <span ref={ref} className={className} data-testid={testId}>
      {prefix}
      {value.toLocaleString("en-GB")}
      {suffix}
    </span>
  );
};

export default CountUp;
